import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { CODIGO, COR_DIFF } from './estilo';
import NavegadorDeHosts from './NavegadorDeHosts';

const ROTA = '/admin/snapshot';

/**
 * Diff do texto cru das duas coletas, no formato unificado de sempre.
 *
 * Complementa o diff estruturado, nao substitui. O estruturado sabe que um
 * vizinho MIGROU de interface e ignora campo volatil; este nao sabe de nada — em
 * compensacao mostra TUDO, inclusive o que nenhum parser cobre. Para comando de
 * nivel 2, ou vendor ainda sem parser, e o unico diff possivel.
 *
 * O preco de mostrar tudo e o ruido: contador de pacote e uptime mudam a cada
 * coleta e aparecem aqui como diferenca. Por isso o padrao e contexto curto, e o
 * aviso na tela diz de onde vem o barulho.
 */
export default function DiffTexto({ antes, depois, empresa, rotuloAntes, rotuloDepois }) {
  const notify = useNotify();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [soMudancas, setSoMudancas] = useState(false);
  const [busca, setBusca] = useState('');
  const [dump, setDump] = useState(null);
  const [carregandoDump, setCarregandoDump] = useState(false);
  const caixa = useRef(null);

  useEffect(() => {
    if (!antes || !depois || antes === depois || !empresa) { setDados(null); return undefined; }
    setDump(null);
    let cancelado = false;
    setCarregando(true);

    api.get(`${ROTA}/run/textdiff/${antes}/${depois}`, { params: { empresa, context: 3 } })
      .then((res) => {
        if (cancelado) return;
        if (res.data?.error) {
          notify.setNotify({ open: true, severity: 'error', message: res.data.error });
          setDados(null);
          return;
        }
        setDados(res.data?.data || null);
      })
      .catch(() => { if (!cancelado) setDados(null); })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
    // notify vem de um Context estável.
  }, [antes, depois, empresa]);

  // Com filtro os indices mudam, entao as secoes sao recalculadas sobre o que
  // esta de fato na tela — senao os atalhos pulariam para o lugar errado.
  const { linhas, secoes } = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const todas = (dados?.diff || []).filter((l) => {
      if (soMudancas && l.t === 'contexto') return false;
      if (termo && !(l.texto || '').toLowerCase().includes(termo)) return false;
      return true;
    });

    if (!soMudancas && !termo) {
      return { linhas: todas, secoes: dados?.sections || [] };
    }

    const secs = [];
    let atual = null;
    todas.forEach((l, idx) => {
      if (l.h !== atual) {
        atual = l.h;
        const orig = (dados?.sections || []).find((x) => x.host_id === l.h);
        secs.push({
          host_id: l.h,
          nome: orig?.nome || `host ${l.h}`,
          inicio: idx,
          diferentes: orig?.diferentes || 0,
        });
      }
    });
    return { linhas: todas, secoes: secs };
  }, [dados, soMudancas, busca]);

  const indicePorLinha = useMemo(() => {
    const m = {};
    secoes.forEach((s, i) => { m[s.inicio] = i; });
    return m;
  }, [secoes]);

  // O dump só é buscado quando o diff sai vazio — no caso comum ele nem é usado.
  useEffect(() => {
    if (!dados?.identical || !depois || dump !== null) return undefined;
    let cancelado = false;
    setCarregandoDump(true);
    api.get(`${ROTA}/run/dump/${depois}`, { params: { empresa } })
      .then((res) => { if (!cancelado) setDump(res.data?.data?.text || ''); })
      .catch(() => { if (!cancelado) setDump(''); })
      .finally(() => { if (!cancelado) setCarregandoDump(false); });
    return () => { cancelado = true; };
  }, [dados, depois, empresa, dump]);

  const copiarDump = async () => {
    try {
      await navigator.clipboard.writeText(dump || '');
      notify.setNotify({ open: true, severity: 'success', message: 'Coleta copiada' });
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'O navegador bloqueou a cópia' });
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(
        (dados?.diff || [])
          .map((l) => `${l.t === 'adicionada' ? '+' : l.t === 'removida' ? '-' : ' '}${l.texto}`)
          .join('\n')
      );
      notify.setNotify({ open: true, severity: 'success', message: 'Diff copiado' });
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'O navegador bloqueou a cópia' });
    }
  };

  if (!antes || !depois || antes === depois) {
    return <Alert severity="info">Escolha duas coletas diferentes nos seletores acima.</Alert>;
  }

  if (carregando) return <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>;
  if (!dados) return null;

  // Idêntico: em vez de uma tela vazia, mostra o texto inteiro.
  //
  // "Nenhuma diferença" é o resultado BOM de uma manutenção, mas sozinho ele não
  // deixa conferir nada. Com o dump ao lado, o operador confirma com os próprios
  // olhos o que foi coletado — e é isso que ele cola no chamado como evidência.
  if (dados.identical) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>
            O texto das duas coletas é byte a byte idêntico
          </Typography>
          <Typography variant="body2">
            Nem os contadores mudaram. Abaixo, a {rotuloDepois || `coleta #${dados.after_run_id}`} inteira.
          </Typography>
        </Alert>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {(dump || '').split('\n').length} linha(s)
          </Typography>
          {carregandoDump && <CircularProgress size={12} />}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
            onClick={copiarDump} disabled={!dump}
          >
            copiar tudo
          </Button>
        </Stack>

        <Box
          sx={{ ...CODIGO, maxHeight: '62vh', overflow: 'auto', whiteSpace: 'pre' }}
        >
          {dump || (carregandoDump ? 'carregando…' : '—')}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`+${dados.added}`}
          sx={{ height: 20, bgcolor: '#16a085', color: '#fff', fontWeight: 700 }} />
        <Chip size="small" label={`−${dados.removed}`}
          sx={{ height: 20, bgcolor: '#c0392b', color: '#fff', fontWeight: 700 }} />
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {rotuloAntes || `#${dados.before_run_id}`} → {rotuloDepois || `#${dados.after_run_id}`}
        </Typography>

        <TextField
          size="small" placeholder="filtrar linhas" sx={{ width: 190 }}
          value={busca} onChange={(e) => setBusca(e.target.value)}
        />

        <Tooltip title="Esconde as linhas de contexto e deixa só o que mudou">
          <FormControlLabel
            control={
              <Switch size="small" checked={soMudancas}
                onChange={(e) => setSoMudancas(e.target.checked)} />
            }
            label={<Typography variant="caption">só as mudanças</Typography>}
          />
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />} onClick={copiar}>
          copiar
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        <Typography variant="caption">
          Este diff é do <strong>texto cru</strong>: mostra tudo, inclusive contador de
          pacote e uptime, que mudam sozinhos. Para o que é mudança de verdade na
          rede, use a comparação estruturada acima.
        </Typography>
      </Alert>

      <NavegadorDeHosts secoes={secoes} containerRef={caixa}>
        {(registrar, cabecalho) => (
          <Box ref={caixa} sx={{ ...CODIGO, p: 0, maxHeight: '62vh', overflow: 'auto' }}>
            {cabecalho}
            {linhas.map((l, i) => {
              const inicioDeSecao = indicePorLinha[i] !== undefined;
              const ordem = secoes.findIndex((s) => s.host_id === l.h);
              const marca = l.t === 'adicionada' ? '+' : l.t === 'removida' ? '-' : ' ';
              return (
                <Box
                  key={i}
                  ref={inicioDeSecao ? registrar(i) : undefined}
                  sx={{
                    whiteSpace: 'pre', px: 0.75,
                    // Fundo alternado por host, sutil: marca a troca de
                    // equipamento para quem rolou rápido.
                    ...(l.t === 'contexto'
                      ? { ...COR_DIFF.contexto, bgcolor: ordem % 2 === 1 ? '#f8fafc' : 'transparent' }
                      : COR_DIFF[l.t]),
                    ...(inicioDeSecao ? { borderTop: '2px solid #cbd5e1' } : {}),
                  }}
                >
                  {marca}{l.texto || ' '}
                </Box>
              );
            })}
            {!linhas.length && (
              <Typography variant="caption" sx={{ color: '#94a3b8', p: 1, display: 'block' }}>
                Nenhuma linha com esse filtro.
              </Typography>
            )}
          </Box>
        )}
      </NavegadorDeHosts>
    </Box>
  );
}
